import { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { api } from '../App';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { 
  ShoppingCart, 
  Coins, 
  Package, 
  Home, 
  TreePine, 
  Shirt,
  Cat,
  Minus,
  Plus,
  CheckCircle
} from 'lucide-react';

// Default styling for categories - will be merged with database categories
const DEFAULT_CATEGORY_STYLES = {
  tools: { icon: Package, color: '#6FCFD3', description: 'Reusable equipment for horse care' },
  feed: { icon: Package, color: '#8B4513', description: 'Food and supplies for your horses' },
  buildings: { icon: Home, color: '#4A90D9', description: 'Barns, stalls, and shelters' },
  fences: { icon: Home, color: '#D66238', description: 'Fences and entry gates' },
  decorations: { icon: TreePine, color: '#228B22', description: 'Trees and decorative items' },
  landscaping: { icon: TreePine, color: '#228B22', description: 'Ground and landscape items' },
  food_water: { icon: Package, color: '#6FCFD3', description: 'Feeding and watering equipment' },
  supplies: { icon: Package, color: '#6FCFD3', description: 'Essential supplies' },
  tack: { icon: Shirt, color: '#D66238', description: 'Saddles and riding equipment' },
  animals: { icon: Cat, color: '#8B4513', description: 'Add new horses to your ranch' },
  structures: { icon: Home, color: '#4A90D9', description: 'Barns, corrals, and shelters' },
};

const Mercantile = ({ user, logout }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userCoins, setUserCoins] = useState(0);
  const [loading, setLoading] = useState(true);
  const [categoriesLoaded, setCategoriesLoaded] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [purchaseQuantity, setPurchaseQuantity] = useState(1);
  const [purchasing, setPurchasing] = useState(false);
  const [showPurchaseDialog, setShowPurchaseDialog] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      const fetchedItems = await fetchItems();
      await fetchCategories(fetchedItems);
    };
    loadData();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await api.get('/mercantile/items');
      const fetchedItems = response.data.items || [];
      setItems(fetchedItems);
      setUserCoins(response.data.user_coins || 0);
      return fetchedItems;
    } catch (error) {
      console.error('Error fetching mercantile items:', error);
      toast.error('Failed to load store items');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async (fetchedItems = []) => {
    let cats = [];
    
    // Try to fetch from API first
    try {
      const response = await api.get('/mercantile/categories');
      cats = response.data.categories || [];
      console.log('Loaded categories from API:', cats);
    } catch (error) {
      console.error('Error fetching categories from API:', error);
    }
    
    // If no categories from API, derive from items
    if (cats.length === 0 && fetchedItems.length > 0) {
      const uniqueCats = [...new Set(fetchedItems.map(i => i.category))].filter(Boolean).sort();
      cats = uniqueCats.map((c, i) => ({ 
        name: c, 
        display_name: c.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        order: i 
      }));
      console.log('Derived categories from items:', cats);
    }
    
    setCategories(cats);
    setCategoriesLoaded(true);
    
    // Set first category as active
    if (cats.length > 0 && !activeCategory) {
      setActiveCategory(cats[0].name);
    }
  };

  // Build category config from database categories
  const getCategoryConfig = () => {
    const config = {};
    console.log('getCategoryConfig called, categories:', categories);
    categories.forEach(cat => {
      const defaults = DEFAULT_CATEGORY_STYLES[cat.name] || { icon: Package, color: '#6FCFD3', description: '' };
      config[cat.name] = {
        label: cat.display_name || cat.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        icon: defaults.icon,
        color: defaults.color,
        description: defaults.description
      };
    });
    console.log('getCategoryConfig returning:', Object.keys(config));
    return config;
  };

  const handlePurchase = async () => {
    if (!selectedItem) return;
    
    setPurchasing(true);
    try {
      const response = await api.post('/mercantile/purchase', {
        item_id: selectedItem.id,
        quantity: purchaseQuantity
      });
      
      toast.success(response.data.message);
      setUserCoins(response.data.new_balance);
      setShowPurchaseDialog(false);
      setSelectedItem(null);
      setPurchaseQuantity(1);
      
      // Refresh items in case stock changed
      fetchItems();
    } catch (error) {
      console.error('Error purchasing item:', error);
      toast.error(error.response?.data?.detail || 'Failed to purchase item');
    } finally {
      setPurchasing(false);
    }
  };

  const openPurchaseDialog = (item) => {
    setSelectedItem(item);
    setPurchaseQuantity(1);
    setShowPurchaseDialog(true);
  };

  const getItemsByCategory = (category) => {
    return items.filter(item => item.category === category);
  };

  const canAfford = (item) => {
    return userCoins >= item.price * purchaseQuantity;
  };

  if (loading || !categoriesLoaded) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#FCFCF8' }}>
        <Navigation user={user} logout={logout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#6FCFD3' }}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FCFCF8' }}>
      <Navigation user={user} logout={logout} />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#D66238' }}>
              🏪 Mercantile
            </h1>
            <p className="text-lg" style={{ color: '#372F2F' }}>
              Stock up on supplies and expand your ranch!
            </p>
          </div>
          
          {/* Coin Balance */}
          <Card className="mt-4 md:mt-0 p-4 flex items-center gap-3" style={{ backgroundColor: '#FFF8E1', border: '2px solid #FFD700' }}>
            <Coins className="w-8 h-8" style={{ color: '#FFD700' }} />
            <div>
              <p className="text-sm font-medium" style={{ color: '#372F2F' }}>Your Balance</p>
              <p className="text-2xl font-bold" style={{ color: '#D66238' }}>{userCoins.toLocaleString()} coins</p>
            </div>
          </Card>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeCategory || categories[0]?.name || 'all'} onValueChange={setActiveCategory} className="w-full">
          <TabsList className="w-full flex flex-wrap justify-start gap-2 bg-transparent h-auto p-0 mb-6">
            {Object.entries(getCategoryConfig()).map(([key, config]) => {
              const Icon = config.icon;
              const itemCount = getItemsByCategory(key).length;
              return (
                <TabsTrigger
                  key={key}
                  value={key}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg border-2 data-[state=active]:border-transparent transition-all"
                  style={{
                    borderColor: activeCategory === key ? config.color : '#e0e0e0',
                    backgroundColor: activeCategory === key ? config.color : 'white',
                    color: activeCategory === key ? 'white' : '#372F2F'
                  }}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{config.label}</span>
                  <Badge variant="secondary" className="ml-1">
                    {itemCount}
                  </Badge>
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* Category Content */}
          {Object.entries(getCategoryConfig()).map(([key, config]) => (
            <TabsContent key={key} value={key} className="mt-0">
              <div className="mb-4">
                <p style={{ color: '#372F2F', opacity: 0.8 }}>{config.description}</p>
              </div>
              
              {getItemsByCategory(key).length === 0 ? (
                <Card className="p-12 text-center">
                  <Package className="w-16 h-16 mx-auto mb-4" style={{ color: '#ccc' }} />
                  <p style={{ color: '#372F2F', opacity: 0.7 }}>
                    No items available in this category yet.
                  </p>
                </Card>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {getItemsByCategory(key).map((item) => (
                    <Card 
                      key={item.id} 
                      className="overflow-hidden hover:shadow-lg transition-shadow"
                      style={{ border: `2px solid ${config.color}` }}
                    >
                      {/* Item Image */}
                      <div 
                        className="h-40 flex items-center justify-center"
                        style={{ backgroundColor: `${config.color}15` }}
                      >
                        {item.image_url ? (
                          <img 
                            src={item.image_url} 
                            alt={item.name}
                            className="max-h-full max-w-full object-contain p-4"
                          />
                        ) : (
                          <config.icon className="w-16 h-16" style={{ color: config.color, opacity: 0.5 }} />
                        )}
                      </div>
                      
                      {/* Item Details */}
                      <div className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-bold text-lg" style={{ color: '#372F2F' }}>
                            {item.name}
                          </h3>
                          {item.subcategory && (
                            <Badge variant="outline" className="text-xs">
                              {item.subcategory}
                            </Badge>
                          )}
                        </div>
                        
                        <p className="text-sm mb-4 line-clamp-2" style={{ color: '#372F2F', opacity: 0.7 }}>
                          {item.description}
                        </p>
                        
                        {/* Price and Buy Button */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-1">
                            <Coins className="w-5 h-5" style={{ color: '#FFD700' }} />
                            <span className="font-bold text-lg" style={{ color: '#D66238' }}>
                              {item.price}
                            </span>
                          </div>
                          
                          <Button
                            onClick={() => openPurchaseDialog(item)}
                            disabled={userCoins < item.price}
                            className="gap-2"
                            style={{ 
                              backgroundColor: userCoins >= item.price ? config.color : '#ccc',
                              color: 'white'
                            }}
                          >
                            <ShoppingCart className="w-4 h-4" />
                            Buy
                          </Button>
                        </div>
                        
                        {/* Stock indicator */}
                        {item.stock !== -1 && (
                          <p className="text-xs mt-2" style={{ color: item.stock > 0 ? '#228B22' : '#D66238' }}>
                            {item.stock > 0 ? `${item.stock} in stock` : 'Out of stock'}
                          </p>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </div>

      {/* Purchase Dialog */}
      <Dialog open={showPurchaseDialog} onOpenChange={setShowPurchaseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ color: '#D66238' }}>
              Purchase {selectedItem?.name}
            </DialogTitle>
            <DialogDescription>
              {selectedItem?.description}
            </DialogDescription>
          </DialogHeader>
          
          {selectedItem && (
            <div className="space-y-6 py-4">
              {/* Quantity Selector (only for inventory items) */}
              {selectedItem.item_type === 'inventory' && (
                <div className="flex items-center justify-center gap-4">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPurchaseQuantity(Math.max(1, purchaseQuantity - 1))}
                    disabled={purchaseQuantity <= 1}
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  
                  <span className="text-2xl font-bold w-12 text-center" style={{ color: '#372F2F' }}>
                    {purchaseQuantity}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => setPurchaseQuantity(purchaseQuantity + 1)}
                    disabled={selectedItem.stock !== -1 && purchaseQuantity >= selectedItem.stock}
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              {/* Total Cost */}
              <div className="p-4 rounded-lg text-center" style={{ backgroundColor: '#FFF8E1' }}>
                <p className="text-sm mb-1" style={{ color: '#372F2F' }}>Total Cost</p>
                <div className="flex items-center justify-center gap-2">
                  <Coins className="w-6 h-6" style={{ color: '#FFD700' }} />
                  <span className="text-3xl font-bold" style={{ color: '#D66238' }}>
                    {(selectedItem.price * purchaseQuantity).toLocaleString()}
                  </span>
                </div>
                <p className="text-sm mt-2" style={{ color: canAfford(selectedItem) ? '#228B22' : '#D66238' }}>
                  {canAfford(selectedItem) 
                    ? `You'll have ${(userCoins - selectedItem.price * purchaseQuantity).toLocaleString()} coins remaining`
                    : `You need ${(selectedItem.price * purchaseQuantity - userCoins).toLocaleString()} more coins`
                  }
                </p>
              </div>
              
              {/* Item type indicator */}
              <div className="text-center text-sm" style={{ color: '#372F2F', opacity: 0.7 }}>
                {selectedItem.item_type === 'inventory' 
                  ? '📦 This item will be added to your inventory'
                  : '🏠 This item will be unlocked for your ranch'
                }
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowPurchaseDialog(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 gap-2"
                  onClick={handlePurchase}
                  disabled={!canAfford(selectedItem) || purchasing}
                  style={{ 
                    backgroundColor: canAfford(selectedItem) ? '#6FCFD3' : '#ccc',
                    color: 'white'
                  }}
                >
                  {purchasing ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Confirm Purchase
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Mercantile;
